import { HttpErrorResponse } from '@angular/common/http';
import { PreorderDeliveryMethod } from './cart.model';
import { Product } from './product.model';
import {
  PERRON_POINTS_COPY,
  withEarlyAccessInsufficient,
  withThreshold,
} from '../perron-points/perron-points-copy';
import { mapEarlyAccessGate403 } from '../perron-points/early-access-gate';

/**
 * Métodos de envío válidos para una precompra. Excluye `'other_shop'`
 * porque las precompras nunca son singles. `Exclude<>` mantiene este tipo
 * acoplado a `PreorderDeliveryMethod`: si checkout añade un método nuevo,
 * TypeScript fuerza una decisión consciente aquí.
 */
export type PrepurchaseDeliveryMethod = Exclude<PreorderDeliveryMethod, 'other_shop'>;

/**
 * Métodos de pago del abono del 30 % que ofrece la precompra web.
 *  - `sinpe`/`transfer` (Bloque 1): comprobante obligatorio en el multipart de
 *    `/new-web-prepurchase`.
 *  - `online` (Bloque 3): tarjeta ONVO. NO viaja por `/new-web-prepurchase` (el
 *    backend lo rechaza): la precompra nace por la pasarela (intent sobre la
 *    reserva → `complete`/webhook), y el modal navega a la página de pago.
 */
export type PrepurchasePaymentMethod = 'sinpe' | 'transfer' | 'online';

/**
 * Payload que enviamos a `POST /new-web-prepurchase` (multipart: viaja junto
 * al comprobante `image`, obligatorio para sinpe/transfer). Espeja las reglas
 * de validación del backend: product_id, quantity, name, surnames, email,
 * phone, delivery_method, payment_method y session_token son obligatorios.
 * Los campos de dirección son obligatorios sólo cuando
 * `delivery_method ∈ {home, ps_delivery}`. `amount_paid` (30 %) lo fija el
 * backend server-side (LayawayPolicy).
 */
export interface PrepurchaseRequest {
  product_id: number;
  quantity: number;
  name: string;
  surnames: string;
  email: string;
  phone: string;
  delivery_method: PrepurchaseDeliveryMethod;
  payment_method: PrepurchasePaymentMethod;
  /**
   * Token de la reserva flotante abierta en `POST /prepurchase-sessions`
   * (Bloque 2): toda creación web consume una sesión. Si no existe o no
   * coincide producto/cantidad, el backend responde 422 (`invalid_session`).
   */
  session_token: string;
  /** Backend lo valida `required` en `/new-web-prepurchase`. 0 cuando no aplica (ps/uber). */
  delivery_price: number;
  identification?: string | null;
  province?: string | null;
  canton?: string | null;
  district?: string | null;
  hood?: string | null;
  directions?: string | null;
  /** Campo libre legacy. No lo usamos desde la web nueva pero el backend lo acepta como nullable. */
  address?: string | null;
}

/**
 * Payload de `POST /prepurchase-sessions` (JSON, Bloque 2): abre la reserva
 * flotante de 15 minutos al pasar al paso de pago (D5). Reserva `quantity`
 * unidades del cupo derivado. El resto de campos es el snapshot del formulario
 * (payload de la sesión), incluido el método que podrá consumirla. Si el usuario
 * cambia de método en el paso de pago, el frontend reemplaza la reserva antes de
 * abrir el comprobante o navegar a ONVO.
 */
export interface PrepurchaseSessionRequest {
  product_id: number;
  quantity: number;
  email: string;
  phone: string;
  delivery_price: number;
  payment_method: PrepurchasePaymentMethod;
  identification?: string | null;
  name?: string | null;
  surnames?: string | null;
  address?: string | null;
  province?: string | null;
  canton?: string | null;
  district?: string | null;
  hood?: string | null;
  directions?: string | null;
  delivery_method?: PrepurchaseDeliveryMethod | null;
  shop_name?: string | null;
}

/**
 * Estados de una sesión de reserva (enum idéntico a `prepurchase_sessions.status`
 * del backend, molde `checkout_sessions.status`). Una `pending` vencida llega
 * ya reportada como `expired` en el GET (la expiración es implícita).
 */
export type PrepurchaseSessionStatus =
  | 'pending'
  | 'consumed'
  | 'expired'
  | 'failed'
  | 'refund_failed';

/**
 * Respuesta de `POST /prepurchase-sessions` (201) y de
 * `GET /prepurchase-sessions/{token}` (rehidratación tras un refresh).
 * `amount_colones` es el 30 % calculado SERVER-SIDE: es el importe
 * autoritativo que muestra la UI si difiere del cálculo cliente.
 */
export interface PrepurchaseSession {
  session_token: string;
  /** ISO 8601 absoluto: el countdown se calcula contra este timestamp. */
  expires_at: string;
  expires_in_seconds: number;
  amount_colones: number;
  currency: string;
  status: PrepurchaseSessionStatus;
  /**
   * Rehidratación de la rama TARJETA (Bloque 3): id del intent ONVO vivo para
   * RE-MONTAR el widget tras un refresh (solo cuando `status='pending'`).
   * Ausente/null si aún no se creó el intent o la reserva no es de tarjeta.
   */
  provider_intent_id?: string | null;
  /**
   * Estado del intento ONVO (`pending|processing|approved`) cuando lo hay: si ya
   * está `approved` (cobrado, aún sin materializar), la página COMPLETA en vez de
   * re-montar el widget.
   */
  provider_intent_status?: string | null;
  /**
   * Precompra creada al aprobarse el cobro de tarjeta (solo cuando
   * `status='consumed'`): éxito terminal.
   */
  prepurchase_id?: number | null;
}

export type PrepurchaseState =
  | 'nuevo'
  | 'pendiente'
  | 'esperando'
  | 'avisar'
  | 'confirmado';

export interface PrepurchaseClient {
  id: number;
  name: string;
  surnames?: string;
  email: string;
  phone?: string;
  [key: string]: unknown;
}

export interface PrepurchaseProductSnapshot {
  id: number;
  name: string;
  [key: string]: unknown;
}

/**
 * Respuesta del backend tras crear una precompra. El modal solo consume
 * `id`, `state`, `total` y los datos del cliente/producto para mostrar
 * confirmación; el resto queda accesible vía indexer.
 */
export interface Prepurchase {
  id: number;
  product_id: number;
  client_id: number;
  state: PrepurchaseState;
  quantity: number;
  name: string;
  sale_price: number;
  subtotal: number;
  tax: number;
  taxes: number;
  total: number;
  amount_paid: number;
  /** Método con el que se pagó/declaró el abono del 30 % (enum del backend). */
  payment_method?: string | null;
  /** URL pública del comprobante subido (accessor `payment_proof_url` del backend). */
  payment_proof_url?: string | null;
  store_pickup?: boolean | null;
  client?: PrepurchaseClient;
  product?: PrepurchaseProductSnapshot;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export type PrepurchaseEnvelope = Prepurchase | { data: Prepurchase };

export function unwrapPrepurchase(payload: PrepurchaseEnvelope): Prepurchase {
  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    (payload as { data: unknown }).data
  ) {
    const maybe = (payload as { data: Prepurchase }).data;
    if (maybe && typeof maybe === 'object' && 'id' in maybe) return maybe;
  }
  return payload as Prepurchase;
}

/**
 * Razones por las que el backend rechaza una precompra en `/new-web-prepurchase`
 * o una reserva en `/prepurchase-sessions`.
 *
 * Mapeo contra `PrepurchaseController.php` y `PrepurchaseSessionController.php`:
 *  - `limit_exceeded`: `quantity > prepurchase_limit` — "máximo de unidades permitidas"
 *  - `stock_exhausted`: `quantity > prepurchase_stock` — "sólo quedan X disponibles"
 *  - `duplicate_monthly`: mismo cliente + producto en los últimos 30 días — "has superado el límite de precompras"
 *  - `sold_out`: 409 — cupo derivado agotado (todas las unidades en proceso de
 *    pago). Se pinta SIEMPRE con el rótulo verbatim del spec (Bloque 2).
 *  - `invalid_session`: 422 — la reserva no existe o no coincide
 *    producto/cantidad ("Tu reserva no es válida. Vuelve a intentarlo."):
 *    el front reabre una sesión nueva.
 *  - `too_many_requests`: 429 — rate-limiting de `POST /prepurchase-sessions`
 *    (hardening Bloque 2). Se pinta con el rótulo verbatim, igual que `sold_out`.
 *  - `validation`: otros 422 (campos requeridos, email inválido, etc.)
 *  - `network`: error de red o 5xx
 *  - `ea_login_required`: 403 `EA_LOGIN_REQUIRED` — gate de Early Access (Perrón
 *    Points), producto `early_access` sin sesión de usuario.
 *  - `ea_insufficient_points`: 403 `EA_INSUFFICIENT_POINTS` — sesión con saldo bajo
 *    el umbral de Early Access.
 *  - `unknown`: fallback
 */
export type PrepurchaseErrorReason =
  | 'limit_exceeded'
  | 'stock_exhausted'
  | 'duplicate_monthly'
  | 'sold_out'
  | 'invalid_session'
  | 'too_many_requests'
  | 'validation'
  | 'network'
  | 'ea_login_required'
  | 'ea_insufficient_points'
  | 'unknown';

export class PrepurchaseError extends Error {
  constructor(
    public readonly reason: PrepurchaseErrorReason,
    message: string,
    /** Umbral EA (Perrón Points) que trajo el 403, solo en `ea_login_required`/`ea_insufficient_points`. */
    public readonly threshold?: number,
  ) {
    super(message);
    this.name = 'PrepurchaseError';
  }
}

/**
 * Adapta el 403 del gate al `PrepurchaseError` que espera el flujo de
 * precompra, con el copy DE PRECOMPRA ya interpolado. Lo usan DOS call sites
 * (`PrepurchaseService` y `PrepurchasePaymentPageComponent`, en
 * `prepurchase-sessions`, `.../payment-intent` y `new-web-prepurchase`).
 *
 * El parseo del contrato del backend ya NO vive aquí: lo hace
 * `mapEarlyAccessGate403` (`shared/perron-points/early-access-gate.ts`), que
 * es neutro y lo comparten carrito y checkout desde que la restricción dejó de
 * ser cosa de precompras (R7). Esta función solo elige el copy.
 *
 * Devuelve `null` cuando el `error` no casa con los dos códigos conocidos o
 * faltan los campos numéricos — el caller cae a su fallback genérico (defensa
 * en profundidad), igual que antes.
 */
export function mapEarlyAccessGateError(err: HttpErrorResponse): PrepurchaseError | null {
  const gate = mapEarlyAccessGate403(err);
  if (!gate || gate.threshold === null) return null;

  if (gate.code === 'EA_LOGIN_REQUIRED') {
    return new PrepurchaseError(
      'ea_login_required',
      withThreshold(PERRON_POINTS_COPY.earlyAccess.guestLocked, gate.threshold),
      gate.threshold,
    );
  }

  if (gate.code === 'EA_INSUFFICIENT_POINTS' && gate.balance !== null) {
    return new PrepurchaseError(
      'ea_insufficient_points',
      withEarlyAccessInsufficient(
        PERRON_POINTS_COPY.earlyAccess.insufficientLocked,
        gate.threshold,
        gate.balance,
      ),
      gate.threshold,
    );
  }

  return null;
}

export type { Product };
